import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    TextField,
    Typography,
} from '@mui/material';
import { Confirm, Report } from 'notiflix';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { ROLE_IDS } from '../../../config/roles';
import { deliverMovesToUser } from '../../../data/api/incomes/IncomeRoutes';
import { getDeliverableUsers } from '../../../data/api/users/UserRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: '#fff',
        '& fieldset': { borderColor: 'rgba(15, 34, 77, 0.14)' },
        '&.Mui-focused fieldset': { borderColor: '#d6aa3a' },
    },
};

const roleLabel = (roleId) => {
    switch (Number(roleId)) {
        case ROLE_IDS.ADMIN: return 'Administrador';
        case ROLE_IDS.JUGADOR: return 'Jugador';
        case ROLE_IDS.DISTRIBUIDOR: return 'Distribuidor';
        default: return 'Usuario';
    }
};

/**
 * Formulario de entrega de jugadas para el ADMIN.
 *
 * Permite entregar jugadas a cualquier usuario que no sea Empresa, mediante
 * un buscador. Si el destinatario es Distribuidor, se habilita el campo de
 * jugadas de regalo y el backend genera una factura por las entregadas. Para
 * el resto (jugadores) solo se acreditan las jugadas.
 */
const AdminDeliveryForm = ({ onDelivered }) => {
    const { token } = useIndex();

    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({ defaultValues: { moves: '', gift_moves: '' } });

    const isDistributor = Number(selectedUser?.role_id) === ROLE_IDS.DISTRIBUIDOR;
    const watchedMoves = Number(watch('moves')) || 0;
    const watchedGift = Number(watch('gift_moves')) || 0;

    // Búsqueda debounced de usuarios entregables.
    useEffect(() => {
        let active = true;
        const term = inputValue.trim();

        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const { data } = await getDeliverableUsers(token, term);
                if (active) setOptions(data?.data ?? []);
            } catch (error) {
                if (active) setOptions([]);
            } finally {
                if (active) setSearching(false);
            }
        }, 300);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [inputValue, token]);

    const numberFieldProps = {
        onWheel: (e) => e.target.blur(),
        onKeyDown: (e) => {
            if (['-', 'e', 'E', '+', '.', ','].includes(e.key)) {
                e.preventDefault();
            }
        },
    };

    const resetForm = () => {
        reset({ moves: '', gift_moves: '' });
        setSelectedUser(null);
        setInputValue('');
    };

    const sendDelivery = async (payload) => {
        setSubmitting(true);
        try {
            const { data } = await deliverMovesToUser(token, payload);
            if (data?.success) {
                Report.success(
                    'Entrega realizada',
                    data?.message ?? 'Jugadas entregadas correctamente.',
                    'Aceptar',
                    { textAlign: 'center' },
                );
                resetForm();
                onDelivered?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = (form) => {
        if (!selectedUser) {
            Report.warning('Atención', 'Selecciona un usuario destinatario.', 'Okay');
            return;
        }

        const moves = Number(form.moves) || 0;
        const giftMoves = isDistributor ? (Number(form.gift_moves) || 0) : 0;

        if (!Number.isInteger(moves) || moves < 0 || !Number.isInteger(giftMoves) || giftMoves < 0) {
            return;
        }
        if (moves + giftMoves < 1) {
            Report.warning('Atención', 'Indica al menos una jugada a entregar.', 'Okay');
            return;
        }

        const payload = {
            user_uuid: selectedUser.uuid,
            moves,
            gift_moves: giftMoves,
        };

        const confirmText = isDistributor
            ? `¿Entregar ${moves} jugada(s)${giftMoves > 0 ? ` y ${giftMoves} de regalo` : ''} a ${selectedUser.name}? Se generará una factura por las jugadas entregadas.`
            : `¿Acreditar ${moves} jugada(s) a ${selectedUser.name}?`;

        Confirm.show(
            'Confirmar entrega',
            confirmText,
            'Sí, entregar',
            'Cancelar',
            () => sendDelivery(payload),
            () => { },
        );
    };

    const optionLabel = (opt) =>
        opt ? `${opt.name ?? ''} ${opt.lastname ?? ''}`.trim() : '';

    return (
        <StyledCard>
            <CardContent sx={{ p: 2.5 }}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Autocomplete
                        fullWidth
                        options={options}
                        value={selectedUser}
                        onChange={(_e, v) => setSelectedUser(v)}
                        inputValue={inputValue}
                        onInputChange={(_e, v) => setInputValue(v)}
                        getOptionLabel={optionLabel}
                        isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
                        loading={searching}
                        loadingText="Buscando…"
                        noOptionsText="Sin resultados"
                        filterOptions={(x) => x}
                        renderOption={(props, opt) => (
                            <li {...props} key={opt.uuid}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{ fontWeight: 700, color: '#10285f' }}>
                                        {opt.name} {opt.lastname}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {roleLabel(opt.role_id)} · Doc. {opt.document ?? '—'} · {opt.email}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Buscar usuario (nombre, cédula o correo)"
                                placeholder="Escribe para buscar…"
                                size="small"
                                sx={fieldStyles}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {searching ? <CircularProgress color="inherit" size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {selectedUser && (
                        <SummaryRow>
                            <Box>
                                <span className="label">Destinatario</span>
                                <strong>{selectedUser.name} {selectedUser.lastname ?? ''}</strong>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                                <Chip
                                    label={roleLabel(selectedUser.role_id)}
                                    size="small"
                                    sx={{
                                        fontWeight: 900,
                                        color: isDistributor ? '#d35400' : '#3490dc',
                                        background: isDistributor ? 'rgba(214, 124, 34, 0.12)' : 'rgba(52, 144, 220, 0.12)',
                                        border: `1px solid ${isDistributor ? 'rgba(214, 124, 34, 0.3)' : 'rgba(52, 144, 220, 0.3)'}`,
                                    }}
                                />
                                <Chip
                                    label={`${Number(selectedUser.moves ?? 0)} jugadas actuales`}
                                    size="small"
                                    sx={{ fontWeight: 800 }}
                                />
                            </Box>
                        </SummaryRow>
                    )}

                    <FieldsGrid $two={isDistributor}>
                        <TextField
                            size="small"
                            type="number"
                            label={isDistributor ? 'Jugadas a entregar' : 'Cantidad de jugadas'}
                            inputProps={{ min: isDistributor ? 0 : 1, step: 1 }}
                            {...numberFieldProps}
                            error={!!errors.moves}
                            helperText={errors.moves?.message ?? (isDistributor ? 'Estas jugadas se facturan.' : '')}
                            sx={fieldStyles}
                            {...register('moves', {
                                required: isDistributor ? false : 'Indica una cantidad.',
                                valueAsNumber: true,
                                min: { value: isDistributor ? 0 : 1, message: isDistributor ? 'No puede ser negativo.' : 'Debe ser mayor a 0.' },
                                validate: (v) => Number.isInteger(Number(v || 0)) || 'Debe ser un número entero.',
                            })}
                        />

                        {isDistributor && (
                            <TextField
                                size="small"
                                type="number"
                                label="Jugadas de regalo"
                                inputProps={{ min: 0, step: 1 }}
                                {...numberFieldProps}
                                error={!!errors.gift_moves}
                                helperText={errors.gift_moves?.message ?? 'Bonus, no se facturan.'}
                                sx={fieldStyles}
                                {...register('gift_moves', {
                                    valueAsNumber: true,
                                    min: { value: 0, message: 'No puede ser negativo.' },
                                    validate: (v) => Number.isInteger(Number(v || 0)) || 'Debe ser un número entero.',
                                })}
                            />
                        )}
                    </FieldsGrid>

                    {isDistributor && (
                        <SummaryBox>
                            <Box className="row">
                                <span><LocalShippingOutlinedIcon fontSize="inherit" /> Entregadas (facturadas)</span>
                                <strong>{watchedMoves}</strong>
                            </Box>
                            <Box className="row">
                                <span><RedeemOutlinedIcon fontSize="inherit" /> De regalo</span>
                                <strong>{watchedGift}</strong>
                            </Box>
                            <Divider sx={{ my: 0.8 }} />
                            <Box className="row total">
                                <span>Total a acreditar</span>
                                <strong>{watchedMoves + watchedGift}</strong>
                            </Box>
                        </SummaryBox>
                    )}

                    <LoadingButton
                        fullWidth
                        type="submit"
                        variant="contained"
                        loading={submitting}
                        disabled={!selectedUser}
                        startIcon={<SendOutlinedIcon />}
                        sx={primaryButtonStyles}
                    >
                        <span>Entregar ahora</span>
                    </LoadingButton>
                </form>
            </CardContent>
        </StyledCard>
    );
};

export default AdminDeliveryForm;

const primaryButtonStyles = {
    mt: 2,
    borderRadius: '12px',
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: '#fff',
    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
    '& .MuiButton-startIcon': { color: '#fff' },
    '&:hover': {
        color: '#fff',
        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
    },
    // Sin esto, MUI pinta el texto del botón deshabilitado en gris oscuro
    // y se pierde sobre el fondo azul. Lo dejamos en blanco translúcido.
    '&.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.7)',
        background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
        opacity: 0.7,
        '& .MuiButton-startIcon': { color: 'rgba(255, 255, 255, 0.7)' },
    },
};

const StyledCard = styled(Card)`
    border-radius: 18px !important;
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.07) !important;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%) !important;
`;

const SummaryRow = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.6rem;
    padding: 0.65rem 0.85rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);
    margin: 1rem 0;

    .label {
        display: block;
        color: rgba(15, 34, 77, 0.6);
        font-size: 0.7rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }

    strong {
        color: #10285f;
        font-size: 0.95rem;
        font-weight: 900;
    }
`;

const FieldsGrid = styled.div`
    display: grid;
    grid-template-columns: ${({ $two }) => ($two ? 'repeat(2, minmax(0, 1fr))' : '1fr')};
    gap: 0.6rem;
    margin-top: 1rem;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const SummaryBox = styled(Box)`
    margin-top: 1rem;
    padding: 0.8rem 1rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.04);
    border: 1px solid rgba(15, 34, 77, 0.1);

    .row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.85rem;
        font-weight: 700;
        color: rgba(15, 34, 77, 0.72);
        padding: 2px 0;

        span {
            display: flex;
            align-items: center;
            gap: 6px;
        }

        strong {
            color: #10285f;
            font-weight: 900;
        }

        &.total {
            font-size: 0.95rem;
            color: #10285f;
        }
    }
`;
